import { useEffect, useRef } from 'react';

/**
 * Keeps the active tab visible in a horizontally scrolling tab strip.
 *
 * On a phone the detail pages' tab strips (project, client) are wider than the
 * screen; opening `?tab=vault` or `#invoices`, or tapping a tab near the edge,
 * left the active one out of sight. Attach the returned ref to the scrolling
 * element and mark the active tab with `aria-current="page"` (or, in a
 * role="tablist", `aria-selected="true"`).
 *
 * Instant, not smooth: a smooth scroll got cut short whenever the page itself
 * moved during it, leaving the tab half in view.
 */
export function useActiveTabInView<T extends HTMLElement>(...deps: unknown[]) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const strip = ref.current;
    const active = strip?.querySelector<HTMLElement>(
      '[aria-current="page"], [aria-selected="true"]',
    );
    if (!strip || !active) return;
    strip.scrollLeft = active.offsetLeft - (strip.clientWidth - active.offsetWidth) / 2;
    // The caller decides when the active tab may have changed.
  }, deps);
  return ref;
}
